import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { LIVE_REFRESH_MS, saveDownload } from "@/lib/reporting";
import { Page, StatusBadge } from "@/components/lims";
import { ArrowLeft, DownloadSimple, Signature } from "@phosphor-icons/react";
import { NotFoundState } from "@/pages/NotFound";

export default function PortalSampleDetail() {
  const { sampleId } = useParams();
  const [sample, setSample] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/portal/samples/${sampleId}`);
      setSample(data);
      setError(null);
    } catch (err) {
      setError({
        status: err.response?.status,
        message: formatApiError(err.response?.data?.detail) || err.message,
      });
    }
  }, [sampleId]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, LIVE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const download = async () => {
    try {
      const { data } = await api.get(`/portal/reports/${sampleId}.pdf`, { responseType: "blob" });
      saveDownload(data, `results-${sample.sample_code}.pdf`, "application/pdf");
      toast.success("Released report downloaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const downloadAcknowledgment = async () => {
    try {
      const { data } = await api.get(`/portal/samples/${sampleId}/signed-acknowledgment.pdf`, { responseType: "blob" });
      saveDownload(data, `acknowledgment-${sample.sample_code}.pdf`, "application/pdf");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (error?.status === 404) {
    return (
      <Page>
        <NotFoundState
          title="Sample not found"
          message="This sample is unavailable. It may not belong to your account or may no longer be shared."
          homeTo="/portal"
          homeLabel="View shared samples"
        />
      </Page>
    );
  }

  return (
    <Page>
      <Link to="/portal" className="inline-flex items-center gap-2 text-sm text-slate-500 mb-4"><ArrowLeft size={16} /> Back to samples</Link>
      {error && (
        <div role="alert" className="rounded-md border border-status-critical/30 bg-status-critical/10 p-3 text-sm text-status-critical">
          <p>{error.message}</p>
          <button type="button" onClick={load} className="mt-2 font-medium underline underline-offset-2">Try again</button>
        </div>
      )}
      {sample && <>
        <div className="flex items-start justify-between mb-5"><div><h1 className="text-3xl font-display font-semibold">{sample.sample_code}</h1><p className="text-sm text-slate-500 mt-1">{sample.sample_type} · live status <StatusBadge status={sample.status} /></p></div>{sample.has_released_report && <button onClick={download} className="inline-flex items-center gap-2 rounded-md bg-slate-900 text-white px-4 py-2 text-sm"><DownloadSimple size={17} /> Download released report</button>}</div>
        {sample.report_acknowledgment && (
          <div className="mb-5 flex items-center gap-3 rounded-md border border-slate-200 bg-white px-4 py-3 text-sm" data-testid="report-acknowledgment">
            <Signature size={18} className="text-slate-400" />
            {sample.report_acknowledgment.status === "pending" ? (
              <>
                <span className="text-slate-600">Please acknowledge receipt of this released report.</span>
                {sample.report_acknowledgment.sign_url && (
                  <a href={sample.report_acknowledgment.sign_url} target="_blank" rel="noreferrer" className="rounded bg-slate-900 px-3 py-1.5 text-xs text-white">Sign acknowledgment</a>
                )}
              </>
            ) : sample.report_acknowledgment.status === "signed" ? (
              <>
                <span className="text-slate-600">Report receipt acknowledged{sample.report_acknowledgment.signed_at && <> on <time dateTime={sample.report_acknowledgment.signed_at} data-timestamp>{new Date(sample.report_acknowledgment.signed_at).toLocaleString()}</time></>}.</span>
                {sample.report_acknowledgment.has_document && (
                  <button onClick={downloadAcknowledgment} data-testid="download-signed-acknowledgment" className="inline-flex items-center gap-1 rounded border border-slate-200 px-3 py-1.5 text-xs hover:border-slate-400">
                    <DownloadSimple size={14} /> Signed PDF
                  </button>
                )}
              </>
            ) : (
              <span className="text-slate-600">Report acknowledgment {sample.report_acknowledgment.status}.</span>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section className="rounded-lg border border-slate-200 bg-white p-5">
            <h2 className="font-display font-medium mb-4">Status history</h2>
            <div className="space-y-4">{sample.history.map((event, index) => (
              <div key={`${event.timestamp}-${index}`} className="border-l-2 border-slate-200 pl-4">
                <StatusBadge status={event.status} />
                <div className="text-xs text-slate-400 mt-1">
                  <time dateTime={event.timestamp} data-timestamp>
                    {new Date(event.timestamp).toLocaleString()}
                  </time>
                </div>
                {event.reason && <div className="text-sm text-slate-600 mt-1">{event.reason}</div>}
              </div>
            ))}</div>
          </section>
          <section className="rounded-lg border border-slate-200 bg-white p-5">
            <h2 className="font-display font-medium mb-4">QA-released results</h2>
            {sample.released_results.length === 0 ? <p className="text-sm text-slate-400">No released results are available.</p> : <div className="space-y-3">{sample.released_results.map((result) => (
              <div key={result.test_code} className="rounded-md bg-slate-50 p-3">
                <div className="text-sm font-medium">{result.assay_name}</div>
                <div className="text-lg mt-1">{result.result_value} {result.result_unit}</div>
                <div className="text-xs text-slate-400 mt-1">
                  Released{" "}
                  <time dateTime={result.released_date} data-timestamp>
                    {new Date(result.released_date).toLocaleString()}
                  </time>
                </div>
              </div>
            ))}</div>}
          </section>
        </div>
      </>}
    </Page>
  );
}
